import { FC, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Stack, TextField, Typography, useMediaQuery } from '@mui/material';

import { CharacterCount } from '@shared/components/CharacterCount';
import { InputSuggestionsPopover } from '@shared/components/InputSuggestionsPopover';


interface Props {
  index: number;
  fieldPath: string;
  placeholder?: string;
  hint?: string;
  suggestions?: string[];
  onFieldChange?: (fieldPath: string, value: any) => void;
}

export const ObjectivesFieldForm: FC<Props> = (props) => {
  const { index, fieldPath, placeholder, hint, suggestions, onFieldChange } = props;
  const { watch } = useFormContext();
  const inputRef = useRef<CVT.MaybeNull<HTMLInputElement>>(null);

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const fieldValue = watch(fieldPath);

  return (
    <Stack gap={1}  width="100%" position="relative">
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} width="100%">
        <Typography variant="body1" fontSize={14} fontWeight={600}>
          Communication Objective {index + 1}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      </Stack>
      <Controller
        name={fieldPath}
        rules={{
          maxLength: {
            value: 80,
            message: 'Maximum length is 80 characters.',
          },
        }}
        render={({ field, fieldState }) => (
          <>
            <TextField
              {...field}
              variant="outlined"
              color="primary"
              inputRef={inputRef}
              fullWidth
              error={!!fieldState.error}
              multiline
              minRows={isMobile ? 2 : 1}
              helperText={(
                <Stack component="span">
                  <span>{fieldState.error?.message}</span>
                  <Stack component="span" direction="row" justifyContent="space-between" width="100%" gap={2}>
                    {fieldValue && <Typography variant="caption" color="text.secondary" fontWeight={500}>Optimal length: 20 - 60 characters</Typography>}
                    <CharacterCount text={field.value} maxLength={80} flexShrink={0} fontWeight={500} ml="auto"/>
                  </Stack>
                </Stack>
              )}
              placeholder={placeholder}
              onChange={(event) => {
                field.onChange(event);
                onFieldChange?.(fieldPath, event.target.value);
              }}
            />
            <InputSuggestionsPopover
              inputRef={inputRef}
              suggestions={suggestions || []}
              label="Suggested Objectives:"
              onSelect={(val) => {
                field.onChange(val);
                onFieldChange?.(fieldPath, val);
              }}
            />
          </>
        )}
      />
    </Stack>
  );
};
